import type {
  AccountJob,
  AccountSearchResult,
  CustomerAccount,
  CustomerRegion,
  CustomerSettingSource,
  ResolvedCustomerSetting,
} from "@wallyo/contracts/console/accounts";
import { SETTINGS, VALUE_TYPES, type Unit } from "@wallyo/contracts/settings";
import type { WorkerCategory } from "@wallyo/contracts/types";
import { t } from "../../i18n";
import { formatDate, formatGnf, formatNumber } from "../../i18n/format";

export type Tone = "ok" | "warn" | "bad" | "info" | "mute";

/** A trade in words. A trade without a catalog entry is a compile error. */
export const tradeName = (trade: WorkerCategory): string => t(`trade.${trade}` as const);

export const regionName = (region: CustomerRegion): string => t(`customers.region.${region}` as const);

/**
 * Where a setting's value comes from, in words: "Personne", "Profil pro",
 * "Tous les pros", "Région Guinée", "Système", "Défaut"…
 */
export function sourceLabel(source: CustomerSettingSource, region?: CustomerRegion): string {
  if (source === "region" && region) return t("customers.source.regionOf", { region: regionName(region) });
  return t(`customers.source.${source}` as const);
}

/** "21/09", with the year when it isn't this year's, in the staff member's language. */
export function formatDay(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  return formatDate(at, {
    day: "2-digit",
    month: "2-digit",
    year: at.getFullYear() === now.getFullYear() ? undefined : "numeric",
  });
}

/** "juin 2026" (fr), "June 2026" (en). */
export function formatMonth(iso: string): string {
  return formatDate(iso, { month: "long", year: "numeric" });
}

/** The name, or "Nom effacé" for a deleted account. */
export function nameOf(person: { name: string | null }): string {
  return person.name ?? t("customers.deletedName");
}

/** A search result's "client or pro" cell: "Client", or "Pro · Plombier · Ratoma". */
export function describeKind(result: Pick<AccountSearchResult, "primaryRole" | "proProfile">): string {
  if (result.proProfile) {
    return [t("customers.kind.pro"), tradeName(result.proProfile.trade), result.proProfile.city].join(" · ");
  }
  return result.primaryRole === "admin" ? t("customers.kind.admin") : t("customers.kind.client");
}

/** The page's kind: every account can post jobs, so someone with a pro profile is "Pro et client". */
export function describeAccountKind(account: CustomerAccount): string {
  if (account.proProfile) return t("customers.kind.proAndClient");
  return account.identity.primaryRole === "admin" ? t("customers.kind.admin") : t("customers.kind.client");
}

export type DisplayedValue = { kind: "on" | "off" | "text"; text: string };

const DEFINITIONS = new Map<string, (typeof SETTINGS)[number]>(SETTINGS.map((setting) => [setting.settingId, setting]));

function oneValue(value: unknown, settingId: string): string {
  const definition = DEFINITIONS.get(settingId);
  const valueType = definition ? VALUE_TYPES[definition.valueType] : undefined;
  if (typeof value === "number") {
    // Any unit a number setting may carry, not only those in use today.
    const unit = (valueType && "unit" in valueType ? valueType.unit : undefined) as Unit | undefined;
    if (unit === "GNF") return formatGnf(value);
    return unit ? t(`unit.${unit}` as const, { value: formatNumber(value) }) : formatNumber(value);
  }
  if (definition?.valueType === "Trade" && typeof value === "string") return tradeName(value as WorkerCategory);
  return String(value);
}

/** A setting's value as the table shows it: a feature on or off, or its value with its unit. */
export function displayValue(setting: Pick<ResolvedCustomerSetting, "settingId" | "value">): DisplayedValue {
  const { value } = setting;
  if (typeof value === "boolean") {
    return value
      ? { kind: "on", text: t("customers.settings.on") }
      : { kind: "off", text: t("customers.settings.off") };
  }
  if (Array.isArray(value)) {
    return {
      kind: "text",
      text: value.length ? value.map((each) => oneValue(each, setting.settingId)).join(", ") : "—",
    };
  }
  return { kind: "text", text: value === "" ? "—" : oneValue(value, setting.settingId) };
}

/**
 * The pill for an on/off value: a feature switched off reads as bad, a
 * preference switched off (low-data mode, an SMS) is just off.
 */
export function valueTone(setting: Pick<ResolvedCustomerSetting, "kind">, value: DisplayedValue): Tone {
  if (value.kind === "on") return "ok";
  return setting.kind === "feature_enablement" ? "bad" : "mute";
}

/** "QUOTE_JOBS_USER_FEATURE_ENABLEMENT" → "QUOTE_JOBS", as the audit log writes it. */
export function shortSettingName(settingId: string): string {
  return settingId.replace(/_USER_(FEATURE_ENABLEMENT|SETTING)$/, "");
}

export type Restriction = { line: string; expiresAt: string | null };

/** The restrictions card: one line per feature switched off for this person, with its reason. */
export function describeRestrictions(settings: readonly ResolvedCustomerSetting[], now?: Date): Restriction[] {
  return settings
    .filter((setting) => setting.restricted)
    .map((setting) => {
      const scope = setting.source === "proProfile" ? t("customers.scope.proProfile") : t("customers.scope.user");
      const line = t("customers.restrictions.line", {
        feature: shortSettingName(setting.settingId),
        scope,
        reason: setting.reason ?? "—",
      });
      return {
        line: setting.expiresAt
          ? `${line} · ${t("customers.restrictions.until", { date: formatDay(setting.expiresAt, now) })}`
          : line,
        expiresAt: setting.expiresAt,
      };
    });
}

const JOB_TONES: Record<AccountJob["status"], Tone> = {
  requested: "warn",
  quoted: "info",
  accepted: "ok",
  cancelled: "mute",
};

export function jobStatus(status: AccountJob["status"]): { label: string; tone: Tone } {
  return { label: t(`customers.jobs.status.${status}` as const), tone: JOB_TONES[status] };
}

/** Who else is on the job: the pro on a client's job, the client on a pro's. */
export function otherParty(job: AccountJob): string {
  if (!job.otherParty) return t("customers.jobs.noPro");
  return job.otherParty.name ?? t("customers.jobs.deletedParty");
}

const VERIFICATION_TONES: Record<CustomerAccount["identity"]["verificationStatus"], Tone> = {
  unverified: "mute",
  pending: "warn",
  verified: "ok",
  rejected: "bad",
};

export function verification(status: CustomerAccount["identity"]["verificationStatus"]): {
  label: string;
  tone: Tone;
} {
  return { label: t(`customers.verification.${status}` as const), tone: VERIFICATION_TONES[status] };
}

const APPROVAL_TONES: Record<NonNullable<CustomerAccount["proProfile"]>["approvalStatus"], Tone> = {
  pending: "warn",
  approved: "ok",
  rejected: "bad",
};

export function approval(status: NonNullable<CustomerAccount["proProfile"]>["approvalStatus"]): {
  label: string;
  tone: Tone;
} {
  return { label: t(`customers.pro.approval.${status}` as const), tone: APPROVAL_TONES[status] };
}

/** "4,6 ★ · 23 avis" (fr), "4.6 ★ · 23 reviews" (en), or no reviews. */
export function rating(pro: NonNullable<CustomerAccount["proProfile"]>): string {
  if (pro.reviewCount === 0) return t("customers.pro.noReviews");
  return t("customers.pro.ratingValue", {
    rating: formatNumber(pro.ratingAvg, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
    count: formatNumber(pro.reviewCount),
  });
}

/** "180 000 GNF / jour" (fr), "GNF 180,000 / day" (en), or "—" when they haven't set one. */
export function rate(pro: NonNullable<CustomerAccount["proProfile"]>): string {
  if (pro.baseRate === null || pro.rateType === null) return "—";
  return t(`customers.rate.${pro.rateType}` as const, { amount: formatGnf(pro.baseRate) });
}

/**
 * "Mensuelle · payée au 30/09", "Mensuelle · terminée", or "Aucune". The
 * statuses are billing's (ADR-0018 §3): a subscription whose renewal was
 * cancelled (`ending`) still runs to `paidUntil`, so it says so.
 */
export function subscription(pro: NonNullable<CustomerAccount["proProfile"]>, now?: Date): string {
  const current = pro.subscription;
  if (!current) return t("customers.pro.noSubscription");
  if (current.paidUntil && current.status === "active") {
    return t("customers.pro.paidUntil", { plan: current.plan, date: formatDay(current.paidUntil, now) });
  }
  if (current.paidUntil && current.status === "ending") {
    return t("customers.pro.endingUntil", { plan: current.plan, date: formatDay(current.paidUntil, now) });
  }
  return t("customers.pro.subscriptionLine", {
    plan: current.plan,
    status: t(`customers.subscription.${current.status}` as const),
  });
}
